import { getDefaultStore } from 'jotai';
import { recentWorkspacesAtom } from '@/features/workspaces/store';
import { AnalysisStatus, WORKSPACE_TYPES, WorkspaceType } from '@/features/workspaces/constants';
import type { AnalysisRunConflictDetail } from '@/features/workspaces/types';
import { t } from '@/features/i18n/utils';
import { toastManager } from '@/ui/components/toast';
import { DOCUMENT_TYPE } from '@/features/documents/constants';

const RECENT_WORKSPACES_LIMIT = 3;

export const resourceTypeToLabel = (type: number) =>
  WORKSPACE_TYPES.find((label) => DOCUMENT_TYPE[label] === type);

export const mapWorkspaceType = <T extends { evaluationType: number | string }>(item: T): T => ({
  ...item,
  evaluationType:
    typeof item.evaluationType === 'number'
      ? (resourceTypeToLabel(item.evaluationType) ?? item.evaluationType)
      : item.evaluationType,
});

export const recentWorkspacesKey = (organizationId: string) =>
  `${organizationId}-recent-workspaces`;

export const trackRecentWorkspace = (
  workspace: { id: string; name: string; type: WorkspaceType; projectId: string },
  organizationId: string,
) =>
  getDefaultStore().set(recentWorkspacesAtom, (prev) => {
    const key = recentWorkspacesKey(organizationId);
    return {
      ...prev,
      [key]: [workspace, ...(prev[key] ?? []).filter((e) => e.id !== workspace.id)].slice(
        0,
        RECENT_WORKSPACES_LIMIT,
      ),
    };
  });

type AnalysisRunConflictStatusValues = {
  failed: number;
  completed: number;
};

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { conflictStatusValues?: AnalysisRunConflictStatusValues };
  }
}

const DEVIATION_STATUS_VALUES: AnalysisRunConflictStatusValues = {
  failed: AnalysisStatus.FAILED,
  completed: AnalysisStatus.DONE,
};

export const showAnalysisRunConflictToast = (
  status: number,
  statusValues: AnalysisRunConflictStatusValues = DEVIATION_STATUS_VALUES,
) => {
  if (status === statusValues.failed)
    return toastManager.add({
      type: 'error',
      title: t({
        en: 'Previous analysis failed. Please try again.',
        nb: 'Forrige analyse feilet. Prøv igjen.',
      }),
    });

  if (status === statusValues.completed)
    return toastManager.add({
      type: 'info',
      title: t({
        en: 'Analysis already completed. Refresh to see the latest results.',
        nb: 'Analysen er allerede fullført. Oppdater for å se de nyeste resultatene.',
      }),
    });

  toastManager.add({
    type: 'info',
    title: t({ en: 'Analysis already running', nb: 'Analysen kjører allerede' }),
  });
};

export const getAnalysisRunConflictDetail = <TStatus>(
  detail: AnalysisRunConflictDetail<TStatus> | { conflict: AnalysisRunConflictDetail<TStatus> },
): AnalysisRunConflictDetail<TStatus> => ('conflict' in detail ? detail.conflict : detail);
